import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from "react";
import { Eye, Loader2, Wand2, CheckCircle2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const scoreColor = (n) => (n >= 80 ? "#10b981" : n >= 60 ? "#f59e0b" : "#f43f5e");

// Visual QA — the Autonomous Art Director. Captures a live screenshot of the game (via the parent's
// GameCanvas.capture()), sends it to a vision model, and shows a look-and-feel score + one-tap
// visual upgrades. Each applied fix goes through the Safe AI Change Engine (undoable). Exposes
// run() so the Compiler can auto-scan after a build finishes.
const VisualQA = forwardRef(function VisualQA({ gameId, capture, onApplied }, ref) {
  const [qa, setQa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null);
  const [done, setDone] = useState({});

  useEffect(() => {
    api.get(`/compiler/${gameId}/visual-qa`).then(({ data }) => { if (data.qa) setQa(data.qa); }).catch(() => {});
  }, [gameId]);

  const run = useCallback(async (opts) => {
    if (loading) return;
    const autoApplyTop = !!(opts && opts.autoApplyTop === true);
    setLoading(true); setDone({});
    try {
      const img = capture ? await capture() : null;
      if (!img) { toast.error("Couldn't grab a screenshot — let the preview finish loading, then retry."); setLoading(false); return; }
      const { data } = await api.post(`/compiler/${gameId}/visual-qa`, { image: img }, { timeout: 90000 });
      setQa(data.qa);
      toast.success(`Art Director looked at your game — visual score ${data.qa.score}.`);
      const top = (data.qa.fixes || [])[0];
      if (autoApplyTop && top) {
        try {
          const res = await api.post(`/compiler/${gameId}/visual-qa/apply`, { instruction: top.instruction }, { timeout: 60000 });
          if (res.data.config && onApplied) onApplied(res.data.config);
          setDone((d) => ({ ...d, [top.instruction]: true }));
          toast.success(`Auto Art Pass: applied "${top.title}" ✨`);
        } catch { /* auto-apply best-effort */ }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Visual QA failed — try again.");
    }
    setLoading(false);
  }, [gameId, capture, loading, onApplied]);

  useImperativeHandle(ref, () => ({ run }), [run]);

  const applyFix = async (fix) => {
    setApplying(fix.instruction);
    try {
      const { data } = await api.post(`/compiler/${gameId}/visual-qa/apply`, { instruction: fix.instruction }, { timeout: 60000 });
      if (data.config && onApplied) onApplied(data.config);
      setDone((d) => ({ ...d, [fix.instruction]: true }));
      toast.success(`Applied: ${fix.title} ✨`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply that fix.");
    }
    setApplying(null);
  };

  const applyAll = async () => {
    for (const f of (qa?.fixes || [])) { if (!done[f.instruction]) await applyFix(f); }
    setTimeout(run, 800);
  };

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="compiler-visualqa">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-bold"><Eye className="w-4 h-4 text-[hsl(var(--primary))]" /> Visual QA · Art Director</div>
        <Button size="sm" variant="outline" className="rounded-full h-7 px-3 text-xs gap-1.5" onClick={run} disabled={loading} data-testid="compiler-visualqa-run">
          {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking…</> : <><Sparkles className="w-3.5 h-3.5" /> {qa ? "Re-scan" : "Scan visuals"} <span className="opacity-70">· 2 cr</span></>}
        </Button>
      </div>
      {!qa && !loading && (
        <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-snug" data-testid="compiler-visualqa-empty">
          The AI Art Director looks at a live screenshot of your game and grades how it <b>looks</b> — composition, colour, lighting, HUD clarity — then suggests concrete visual upgrades you can apply in one tap.
        </p>
      )}
      {qa && (
        <div className="space-y-2.5" data-testid="compiler-visualqa-report">
          <div className="flex items-center gap-3">
            <div className="text-3xl font-extrabold tabular-nums" style={{ color: scoreColor(qa.score) }} data-testid="compiler-visualqa-score">{qa.score}</div>
            <p className="text-[12px] leading-snug flex-1">{qa.verdict}</p>
          </div>
          {(qa.observations || []).length > 0 && (
            <ul className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug list-disc pl-4 space-y-0.5">
              {qa.observations.map((o, i) => (<li key={i}>{o}</li>))}
            </ul>
          )}
          {(qa.fixes || []).length > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Suggested visual upgrades</div>
                <button className="text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline disabled:opacity-50" onClick={applyAll} disabled={!!applying} data-testid="compiler-visualqa-applyall">Apply all</button>
              </div>
              {qa.fixes.map((f, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1.5" data-testid={`compiler-visualqa-fix-${i}`}>
                  <Wand2 className="w-3.5 h-3.5 mt-0.5 text-[hsl(var(--primary))] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-medium">{f.title}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug">{f.instruction}</div>
                  </div>
                  {done[f.instruction] ? (
                    <span className="text-emerald-500 text-[11px] font-semibold inline-flex items-center gap-0.5 shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /> done</span>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] shrink-0" onClick={() => applyFix(f)} disabled={applying === f.instruction} data-testid={`compiler-visualqa-apply-${i}`}>
                      {applying === f.instruction ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
                    </Button>
                  )}
                </div>
              ))}
            </>
          ) : (
            <p className="text-[12px] text-emerald-500 font-medium">Looks great — the Art Director had no visual fixes! 🌟</p>
          )}
          {qa.at && <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Scanned {new Date(qa.at).toLocaleString()}</p>}
        </div>
      )}
    </div>
  );
});

export default VisualQA;
